<template>
  <div class="word-unscramble-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Awesome!"
    />
    <div v-else>
      <h1 class="word-image" @click="speakOutLoud(targetWord)">
        {{ wordEmoji }}
      </h1>
      <p v-if="message" class="message">
        {{ message }}
      </p>

      <!-- Slots for the letters -->
      <div class="letter-slots">
        <div
          v-for="(slotLetter, index) in slotLetters"
          :key="index"
          :ref="`slot-${index}`"
          class="letter-slot"
          @dragover.prevent
          @drop="handleDrop($event, index)"
        >
          <span v-if="slotLetter">{{ slotLetter }}</span>
        </div>
      </div>

      <!-- Scrambled letters at the bottom -->
      <div class="scrambled-letters">
        <div
          v-for="(letter, index) in scrambledLetters"
          :key="index"
          class="letter-draggable"
          :style="draggedLetter === letter ? draggedStyle : {}"
          draggable="true"
          @dragstart="handleDrag($event, letter)"
          @click="speakOutLoud(letter.toLowerCase())"
          @touchstart="handleTouchStart($event, letter)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ letter }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin"; // update the path as needed

export default {
  mixins: [speechMixin],
  components: {
    LevelComplete,
  },
  props: {
    mode: {
      type: String,
      default: 'words',
      validator: (value) => ['fruits', 'words'].includes(value),
    },
  },
  data() {
    return {
      fruitsAndVeggiesWords: [
        { word: "APPLE", emoji: "🍎" },
        { word: "BANANA", emoji: "🍌" },
        { word: "GRAPE", emoji: "🍇" },
        { word: "WATERMELON", emoji: "🍉" },
        { word: "CARROT", emoji: "🥕" },
        { word: "BROCCOLI", emoji: "🥦" },
        { word: "PEACH", emoji: "🍑" },
        { word: "LETTUCE", emoji: "🥬" },
      ],
      generalWords: [
        { word: "HOUSE", emoji: "🏠" },
        { word: "BICYCLE", emoji: "🚲" },
        { word: "BOOK", emoji: "📚" },
        { word: "PHONE", emoji: "📱" },
        { word: "CHAIR", emoji: "🪑" },
        { word: "CLOCK", emoji: "🕰️" },
        { word: "MIRROR", emoji: "🪞" },
      ],
      targetWord: "",
      wordEmoji: "",
      scrambledLetters: [],
      initialScrambledLetters: [],
      slotLetters: [],
      message: "",
      isGameComplete: false,
      draggedLetter: null,
      draggedStyle: {},
      completedWords: new Set(),
      words: [], // Will be set based on mode
    };
  },
  mounted() {
    // Initialize voices from the mixin
    window.speechSynthesis.onvoiceschanged = this.setupVoices;
    this.setupVoices();
    
    // Initialize words based on mode
    this.words = this.mode === 'fruits' ? this.fruitsAndVeggiesWords : this.generalWords;
    this.setupGame();
  },
  methods: {
    setupGame() {
      if (this.completedWords.size === this.words.length) {
        this.isGameComplete = true;
        this.speakOutLoud("Awesome!");
        setTimeout(() => {
          if (this.mode === 'fruits') {
            this.$router.push("/fruits-and-veggies");
          } else {
            this.$router.push("/words");
          }
        }, 4000);
        return;
      }

      const availableWords = this.words.filter(
        (wordObj) => !this.completedWords.has(wordObj.word)
      );

      if (availableWords.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableWords.length);
        this.targetWord = availableWords[randomIndex].word;
        this.wordEmoji = availableWords[randomIndex].emoji;
        this.speakOutLoud(this.targetWord);
      } else {
        console.error("No available words to select.");
        return;
      }

      this.scrambledLetters = this.shuffleArray(this.targetWord.split(""));
      this.initialScrambledLetters = [...this.scrambledLetters];
      this.slotLetters = Array(this.targetWord.length).fill(null);
      this.message = "";
    },
    shuffleArray(array) {
      let currentIndex = array.length, temporaryValue, randomIndex;

      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }

      return array;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    handleDrag(event, letter) {
      event.dataTransfer.setData("text/plain", letter);
    },
    handleDrop(event, index) {
      const droppedLetter = event.dataTransfer.getData("text/plain");
      const correctLetter = this.targetWord[index];

      if (!this.slotLetters[index]) {
        if (droppedLetter === correctLetter) {
          this.slotLetters.splice(index, 1, droppedLetter);
          this.removeFromScrambledLetters(droppedLetter);
          this.message = "";
          this.speakOutLoud(droppedLetter.toLowerCase());

          if (this.isWordComplete()) {
            this.playSound(
              "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
            );
            this.message = "✅ Correct!";
            this.speakOutLoud(this.targetWord);
            this.completedWords.add(this.targetWord);
            setTimeout(() => {
              this.setupGame();
            }, 2000);
          }
        } else {
          this.message = "❌";
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      } else {
        this.message = "❌";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    removeFromScrambledLetters(letter) {
      const index = this.scrambledLetters.indexOf(letter);
      if (index > -1) {
        this.scrambledLetters.splice(index, 1);
      }
    },
    handleTouchStart(event, letter) {
      this.draggedLetter = letter;
      this.draggedStyle = {
        position: "absolute",
        left: `${event.touches[0].clientX}px`,
        top: `${event.touches[0].clientY}px`,
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault();
      this.draggedStyle.left = `${event.touches[0].clientX}px`;
      this.draggedStyle.top = `${event.touches[0].clientY}px`;
    },
    handleTouchEnd(event) {
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;

      for (let i = 0; i < this.slotLetters.length; i++) {
        const slotEl = this.$refs[`slot-${i}`][0];
        const rect = slotEl.getBoundingClientRect();

        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          this.handleDrop(
            { dataTransfer: { getData: () => this.draggedLetter } },
            i
          );
          break;
        }
      }

      this.draggedLetter = null;
      this.draggedStyle = {};
    },
    isWordComplete() {
      return !this.slotLetters.includes(null);
    },
    getCurrentWord() {
      return this.slotLetters.join("");
    },
    resetCurrentAttempt() {
      this.slotLetters = Array(this.targetWord.length).fill(null);
      this.scrambledLetters = [...this.initialScrambledLetters];
      this.message = "";
    },
  },
};
</script>

<style scoped>
.word-unscramble-game {
  text-align: center;
}

.word-image {
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  cursor: pointer;
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}

/* Modified styles for letter-slots to make it responsive */
.letter-slots {
  display: grid;
  max-width: 500px;
  margin: auto;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 1rem;
  margin-bottom: 30px;
}

.letter-slot {
  width: 60px;
  height: 80px;
  border: 2px dashed gray;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: lightgray;
  border-radius: 10px;
  font-size: 2rem;
  text-transform: uppercase;
}

.scrambled-letters {
  max-width: 500px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 1rem;
  margin-top: 20px;
}

.letter-draggable {
  cursor: grab;
  max-width: 50px;
  border-radius: 10px;
  padding: 10px;
  font-size: 2rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
  text-transform: uppercase;
}

.letter-draggable:hover {
  transform: scale(1.1);
}
</style>
